export interface QRGenerationOptions {
  value: string;
  size?: number;
  backgroundColor?: string;
  foregroundColor?: string;
  canvasSize?: number;
  padding?: number;
  cornerRadius?: number;
}

/**
 * Generates a QR code image as a blob from an SVG element
 * @param svgElement - The SVG element containing the QR code
 * @param options - Configuration options for the QR code generation
 * @returns Promise that resolves to a Blob containing the PNG image
 */
export const generateQRCodeImageFromSVG = async (
  svgElement: SVGElement,
  options: {
    canvasSize?: number;
    backgroundColor?: string;
    padding?: number;
    cornerRadius?: number;
    fileName?: string;
  } = {}
): Promise<Blob> => {
  const {
    canvasSize = 480,
    backgroundColor = '#ffffff',
    padding = 40,
    cornerRadius = 32
  } = options;

  return new Promise((resolve, reject) => {
    try {
      // Convert SVG to string
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      // Create an image from the SVG
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = canvasSize;
        canvas.height = canvasSize;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // White background with rounded corners
        ctx.fillStyle = backgroundColor;
        ctx.beginPath();
        ctx.roundRect(0, 0, canvasSize, canvasSize, cornerRadius);
        ctx.fill();

        // QR code in center
        const qrSize = canvasSize - (padding * 2);
        ctx.drawImage(img, padding, padding, qrSize, qrSize);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/png');
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Downloads a QR code as a PNG file
 * @param svgElement - The SVG element containing the QR code
 * @param fileName - The name for the downloaded file
 * @param options - Configuration options for the QR code generation
 */
export const downloadQRCode = async (
  svgElement: SVGElement,
  fileName: string,
  options: {
    canvasSize?: number;
    backgroundColor?: string;
    padding?: number;
    cornerRadius?: number;
  } = {}
): Promise<void> => {
  try {
    const blob = await generateQRCodeImageFromSVG(svgElement, options);

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = fileName;
    link.href = url;
    link.click();

    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading QR code:', error);
    throw error;
  }
};

/**
 * Generates a QR code blob for use in ZIP files
 * @param svgElement - The SVG element containing the QR code
 * @param options - Configuration options for the QR code generation
 * @returns Promise that resolves to a Blob containing the PNG image
 */
export const generateQRCodeBlob = async (
  svgElement: SVGElement,
  options: {
    canvasSize?: number;
    backgroundColor?: string;
    padding?: number;
    cornerRadius?: number;
  } = {}
): Promise<Blob> => {
  return generateQRCodeImageFromSVG(svgElement, options);
};
